/** Rate Plan Creation Dialog component. */
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import apiClient from "../../api/client";

/** API base URL. */
const API_BASE_URL = process.env.REACT_APP_API_URL || "";

/** Props for RatePlanCreateDialog. */
interface RatePlanCreateDialogProps {
  /** Whether dialog is open */
  open: boolean;
  /** Close dialog callback */
  onClose: () => void;
  /** Callback after successful creation */
  onCreateSuccess?: (ratePlanId: string) => void;
}

/** Rate Plan Create Dialog component. */
export default function RatePlanCreateDialog({
  open,
  onClose,
  onCreateSuccess,
}: RatePlanCreateDialogProps) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [createdRatePlanId, setCreatedRatePlanId] = useState<string | null>(null);

  /** Reset form on open. */
  useEffect(() => {
    if (open) {
      setName("");
      setDescription("");
      setError("");
      setSuccess(false);
      setCreatedRatePlanId(null);
    }
  }, [open]);

  /** Validate form. */
  const validateForm = (): boolean => {
    if (!name.trim()) {
      setError("Rate plan name is required");
      return false;
    }

    if (name.trim().length < 3) {
      setError("Rate plan name must be at least 3 characters");
      return false;
    }

    return true;
  };

  /** Handle create. */
  const handleCreate = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      // apiClient injects Authorization + X-CSRF-Token (raw fetch 403s on CSRF)
      const response = await apiClient.post("/api/v1/rate-plans/", {
        name: name.trim(),
        description: description.trim() || null,
      });

      const data = response.data;
      setCreatedRatePlanId(data.id);
      setSuccess(true);

      // Call success callback
      if (onCreateSuccess) {
        onCreateSuccess(data.id);
      }

      // Redirect to detail page after showing success message
      setTimeout(() => {
        navigate(`/rate-plans/${data.id}`);
        handleClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || "Failed to create rate plan");
    } finally {
      setLoading(false);
    }
  };

  /** Handle close. */
  const handleClose = () => {
    if (!loading) {
      setName("");
      setDescription("");
      setError("");
      setSuccess(false);
      setCreatedRatePlanId(null);
      onClose();
    }
  };

  /** Handle key press. */
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !loading) {
      handleCreate();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AddIcon />
          <Typography variant="h6" component="span">
            Create Rate Plan
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
          {/* Name Field */}
          <TextField
            label="Rate Plan Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyPress={handleKeyPress}
            fullWidth
            required
            autoFocus
            placeholder="e.g., Wholesale Premium 2024"
            disabled={loading || success}
            error={!!error && !name.trim()}
            helperText={error && !name.trim() ? error : "Enter a unique name for this rate plan"}
            inputProps={{ minLength: 3 }}
          />

          {/* Description Field */}
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onKeyPress={handleKeyPress}
            fullWidth
            multiline
            rows={4}
            placeholder="Optional description of this rate plan..."
            disabled={loading || success}
            helperText="Optional: Add details about this rate plan's purpose or target customers"
          />

          {/* Character Count */}
          <Typography variant="caption" color="text.secondary">
            {name.length}/100 characters (minimum 3)
          </Typography>

          {/* Success Message */}
          {success && (
            <Alert
              severity="success"
              icon={<CheckCircleIcon />}
              sx={{ mt: 2 }}
            >
              Rate plan created successfully! Redirecting to detail page...
            </Alert>
          )}

          {/* Error Message */}
          {error && name.trim() && (
            <Alert severity="error" onClose={() => setError("")} sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={loading || success}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={loading || success}
          startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
        >
          {loading ? "Creating..." : "Create Rate Plan"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
